/// Zustand store for editor state: tabs, active tab, selection, viewport,
/// tool mode, and undo/redo state.
///
/// Supports per-panel tab isolation for split-view layouts. Each panel
/// ("first" / "second") maintains its own tab list and active tab.
/// In single-layout mode a virtual "single" panel owns all tabs.

import { create } from "zustand";
import type { EditorTab, SelectionInfo, ViewportInfo, ToolMode, EditorState, HoveredGroupInfo } from "@/types/editor";

// ---------------------------------------------------------------------------
// Tool mode helpers
// ---------------------------------------------------------------------------

/** Convert ToolMode string to the integer expected by the C++ flowy_set_tool_mode ABI. */
function toolModeToInt(mode: ToolMode): number {
  switch (mode) {
    case "select":
      return 0;
    case "pan":
      return 1;
    case "addConnection":
      return 2;
    default:
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Panel identity
// ---------------------------------------------------------------------------

/** Identifies a panel pane in split or single layout. */
export type PanelId = "single" | "first" | "second";

// ---------------------------------------------------------------------------
// Store state shape
// ---------------------------------------------------------------------------

interface EditorStoreState {
  /** All open editor tabs (flat list, source of truth for tab data). */
  tabs: EditorTab[];

  /**
   * ID of the currently active tab (global, for toolbar / property panel).
   * Always matches the active tab in the focused panel.
   */
  activeTabId: string | null;

  /** Per-tab editor state (selection, viewport, tool mode). */
  editorStates: Map<string, EditorState>;

  // -- Per-panel state ---

  /** Ordered tab IDs per panel. */
  panelTabs: Record<PanelId, string[]>;

  /** Active tab ID per panel. */
  panelActiveTabId: Record<PanelId, string | null>;

  /** Which panel currently has focus (receives new tabs, toolbar actions). */
  focusedPanel: PanelId;

  /** Contextual connection mode hint displayed in the status bar. Empty when not in connect mode. */
  connectionHint: string;

  /** Pending connect-and-create source info (set when drag ends on empty canvas). */
  pendingConnectSource: {
    sourceId: number;
    dropX: number;
    dropY: number;
  } | null;
}

interface EditorStoreActions {
  // Tab management
  addTab: (tab: EditorTab, targetPanel?: PanelId) => void;
  removeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTab: (tabId: string, updates: Partial<EditorTab>) => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;

  // Panel management
  setFocusedPanel: (panelId: PanelId) => void;
  assignTabToPanel: (tabId: string, panelId: PanelId) => void;
  setPanelActiveTab: (panelId: PanelId, tabId: string) => void;
  distributeTabs: (mode: "single" | "split") => void;
  getPanelForTab: (tabId: string) => PanelId | null;
  getActiveTabForPanel: (panelId: PanelId) => string | null;

  // Selection
  setSelection: (tabId: string, selection: SelectionInfo) => void;
  clearSelection: (tabId: string) => void;

  // Viewport
  setViewport: (tabId: string, viewport: ViewportInfo) => void;

  // Tool mode
  setToolMode: (tabId: string, mode: ToolMode) => void;

  // Undo/redo state
  setUndoRedoState: (tabId: string, canUndo: boolean, canRedo: boolean) => void;

  // Dirty state
  setDirty: (tabId: string, dirty: boolean) => void;

  // Worker association
  setWorkerForTab: (tabId: string, workerId: string) => void;

  // Convenience getters
  getActiveTab: () => EditorTab | undefined;
  getActiveEditorState: () => EditorState | undefined;
  getEditorState: (tabId: string) => EditorState | undefined;

  // Connection mode
  setConnectionHint: (hint: string) => void;
  setPendingConnectSource: (
    info: { sourceId: number; dropX: number; dropY: number } | null,
  ) => void;

  // Collapse state (Phase 26, Plan 02)
  setCollapseState: (tabId: string, groupId: number, collapsed: boolean, childNames: string[]) => void;
  setHoveredGroup: (tabId: string, hoveredGroup: HoveredGroupInfo | null) => void;

  // Reset
  reset: () => void;
}

type EditorStore = EditorStoreState & EditorStoreActions;

// ---------------------------------------------------------------------------
// Default editor state for a new tab
// ---------------------------------------------------------------------------

function createDefaultEditorState(tabId: string): EditorState {
  return {
    tabId,
    selection: { selectedIds: [] },
    viewport: { zoom: 1.0, centerX: 0, centerY: 0 },
    toolMode: "select",
  };
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialState: EditorStoreState = {
  tabs: [],
  activeTabId: null,
  editorStates: new Map(),
  panelTabs: { single: [], first: [], second: [] },
  panelActiveTabId: { single: null, first: null, second: null },
  focusedPanel: "single",
  connectionHint: "",
  pendingConnectSource: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Find which panel a tab belongs to. */
function findPanelForTab(panelTabs: Record<PanelId, string[]>, tabId: string): PanelId | null {
  if (panelTabs.single.includes(tabId)) return "single";
  if (panelTabs.first.includes(tabId)) return "first";
  if (panelTabs.second.includes(tabId)) return "second";
  return null;
}

/** Remove a tabId from all panel lists, return updated copy. */
function removeTabFromPanels(
  panelTabs: Record<PanelId, string[]>,
  tabId: string,
): Record<PanelId, string[]> {
  return {
    single: panelTabs.single.filter((id) => id !== tabId),
    first: panelTabs.first.filter((id) => id !== tabId),
    second: panelTabs.second.filter((id) => id !== tabId),
  };
}

/** Pick next active tab for a panel after removing one. */
function pickNextActiveTab(
  panelTabIds: string[],
  removedId: string,
  currentActiveId: string | null,
): string | null {
  if (currentActiveId !== removedId) return currentActiveId;
  const remaining = panelTabIds.filter((id) => id !== removedId);
  if (remaining.length === 0) return null;
  const removedIdx = panelTabIds.indexOf(removedId);
  return remaining[Math.min(removedIdx, remaining.length - 1)];
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useEditorStore = create<EditorStore>((set, get) => ({
  ...initialState,

  // -------------------------------------------------------------------------
  // Tab management
  // -------------------------------------------------------------------------

  addTab: (tab, targetPanel) =>
    set((state) => {
      const editorStates = new Map(state.editorStates);
      editorStates.set(tab.id, createDefaultEditorState(tab.id));

      // Determine which panel receives the tab
      const panel = targetPanel ?? state.focusedPanel;

      const panelTabs = { ...state.panelTabs };
      panelTabs[panel] = [...panelTabs[panel], tab.id];

      const panelActiveTabId = { ...state.panelActiveTabId };
      panelActiveTabId[panel] = tab.id;

      return {
        tabs: [...state.tabs, tab],
        activeTabId: tab.id,
        editorStates,
        panelTabs,
        panelActiveTabId,
      };
    }),

  removeTab: (tabId) =>
    set((state) => {
      const tabs = state.tabs.filter((t) => t.id !== tabId);
      const editorStates = new Map(state.editorStates);
      editorStates.delete(tabId);

      // Update panel assignments
      const panelTabs = removeTabFromPanels(state.panelTabs, tabId);

      // Update per-panel active tabs
      const panelActiveTabId = { ...state.panelActiveTabId };
      for (const pid of ["single", "first", "second"] as PanelId[]) {
        panelActiveTabId[pid] = pickNextActiveTab(
          state.panelTabs[pid],
          tabId,
          state.panelActiveTabId[pid],
        );
      }

      // Update global activeTabId
      let activeTabId = state.activeTabId;
      if (activeTabId === tabId) {
        // Prefer the focused panel's new active tab
        activeTabId = panelActiveTabId[state.focusedPanel] ?? null;
        // If focused panel is empty, try the other panel
        if (!activeTabId) {
          for (const pid of ["single", "first", "second"] as PanelId[]) {
            if (panelActiveTabId[pid]) {
              activeTabId = panelActiveTabId[pid];
              break;
            }
          }
        }
      }

      return { tabs, activeTabId, editorStates, panelTabs, panelActiveTabId };
    }),

  setActiveTab: (tabId) =>
    set((state) => {
      // Find which panel owns this tab and update that panel's active
      const panel = findPanelForTab(state.panelTabs, tabId);
      if (!panel) return { activeTabId: tabId };

      const panelActiveTabId = { ...state.panelActiveTabId };
      panelActiveTabId[panel] = tabId;

      return {
        activeTabId: tabId,
        panelActiveTabId,
        focusedPanel: panel,
      };
    }),

  updateTab: (tabId, updates) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => (tab.id === tabId ? { ...tab, ...updates } : tab)),
    })),

  reorderTabs: (fromIndex, toIndex) =>
    set((state) => {
      const tabs = [...state.tabs];
      const [moved] = tabs.splice(fromIndex, 1);
      tabs.splice(toIndex, 0, moved);
      return { tabs };
    }),

  // -------------------------------------------------------------------------
  // Panel management
  // -------------------------------------------------------------------------

  setFocusedPanel: (panelId) =>
    set((state) => {
      // Update global activeTabId to match focused panel's active tab
      const activeTabId = state.panelActiveTabId[panelId] ?? state.activeTabId;
      return { focusedPanel: panelId, activeTabId };
    }),

  assignTabToPanel: (tabId, panelId) =>
    set((state) => {
      const panelTabs = removeTabFromPanels(state.panelTabs, tabId);
      panelTabs[panelId] = [...panelTabs[panelId], tabId];

      const panelActiveTabId = { ...state.panelActiveTabId };
      // If the target panel has no active tab, make this one active
      if (!panelActiveTabId[panelId]) {
        panelActiveTabId[panelId] = tabId;
      }

      // Clean up active tab on source panels (if it was the removed tab)
      for (const pid of ["single", "first", "second"] as PanelId[]) {
        if (pid !== panelId && panelActiveTabId[pid] === tabId) {
          panelActiveTabId[pid] = panelTabs[pid][0] ?? null;
        }
      }

      return { panelTabs, panelActiveTabId };
    }),

  setPanelActiveTab: (panelId, tabId) =>
    set((state) => {
      const panelActiveTabId = { ...state.panelActiveTabId };
      panelActiveTabId[panelId] = tabId;

      // If this is the focused panel, also update global active
      const activeTabId = panelId === state.focusedPanel ? tabId : state.activeTabId;

      return { panelActiveTabId, activeTabId };
    }),

  distributeTabs: (mode) =>
    set((state) => {
      const allTabIds = state.tabs.map((t) => t.id);

      if (mode === "single") {
        // Consolidate all tabs into "single" panel
        return {
          panelTabs: { single: allTabIds, first: [], second: [] },
          panelActiveTabId: {
            single: state.activeTabId,
            first: null,
            second: null,
          },
          focusedPanel: "single",
        };
      }

      // Split mode: first panel gets first tab, second gets the rest
      // (or if already split, keep existing distribution)
      const existingFirst = state.panelTabs.first;
      const existingSecond = state.panelTabs.second;

      // If panels already have tabs (re-entering split), keep distribution
      if (existingFirst.length > 0 || existingSecond.length > 0) {
        // Just ensure all tabs are assigned
        const assigned = new Set([...existingFirst, ...existingSecond]);
        const unassigned = allTabIds.filter((id) => !assigned.has(id));
        const focusedPanel = state.focusedPanel === "single" ? "first" : state.focusedPanel;

        const panelTabs = {
          single: [],
          first: [...existingFirst],
          second: [...existingSecond],
        };

        // Unassigned tabs go to the focused panel
        panelTabs[focusedPanel].push(...unassigned);

        // Remove any tabs from panels that no longer exist
        const validIds = new Set(allTabIds);
        panelTabs.first = panelTabs.first.filter((id) => validIds.has(id));
        panelTabs.second = panelTabs.second.filter((id) => validIds.has(id));

        const panelActiveTabId = {
          single: null,
          first:
            state.panelActiveTabId.first && panelTabs.first.includes(state.panelActiveTabId.first)
              ? state.panelActiveTabId.first
              : (panelTabs.first[0] ?? null),
          second:
            state.panelActiveTabId.second &&
            panelTabs.second.includes(state.panelActiveTabId.second)
              ? state.panelActiveTabId.second
              : (panelTabs.second[0] ?? null),
        };

        return {
          panelTabs,
          panelActiveTabId,
          focusedPanel,
          activeTabId: panelActiveTabId[focusedPanel] ?? state.activeTabId,
        };
      }

      // Fresh split: first tab goes to first panel, rest to second
      const firstTabIds = allTabIds.length > 0 ? [allTabIds[0]] : [];
      const secondTabIds = allTabIds.length > 1 ? allTabIds.slice(1) : [];

      const panelActiveTabId = {
        single: null,
        first: firstTabIds[0] ?? null,
        second: secondTabIds[0] ?? null,
      };

      // Focus the panel that has the currently active tab, or first panel
      let focusedPanel: PanelId = "first";
      if (state.activeTabId && secondTabIds.includes(state.activeTabId)) {
        focusedPanel = "second";
      }

      return {
        panelTabs: {
          single: [],
          first: firstTabIds,
          second: secondTabIds,
        },
        panelActiveTabId,
        focusedPanel,
        activeTabId: panelActiveTabId[focusedPanel] ?? state.activeTabId,
      };
    }),

  getPanelForTab: (tabId) => {
    return findPanelForTab(get().panelTabs, tabId);
  },

  getActiveTabForPanel: (panelId) => {
    return get().panelActiveTabId[panelId];
  },

  // -------------------------------------------------------------------------
  // Selection
  // -------------------------------------------------------------------------

  setSelection: (tabId, selection) =>
    set((state) => {
      const editorStates = new Map(state.editorStates);
      const current = editorStates.get(tabId) ?? createDefaultEditorState(tabId);
      editorStates.set(tabId, { ...current, selection });
      return { editorStates };
    }),

  clearSelection: (tabId) =>
    set((state) => {
      const editorStates = new Map(state.editorStates);
      const current = editorStates.get(tabId) ?? createDefaultEditorState(tabId);
      editorStates.set(tabId, { ...current, selection: { selectedIds: [] } });
      return { editorStates };
    }),

  // -------------------------------------------------------------------------
  // Viewport
  // -------------------------------------------------------------------------

  setViewport: (tabId, viewport) =>
    set((state) => {
      const editorStates = new Map(state.editorStates);
      const current = editorStates.get(tabId) ?? createDefaultEditorState(tabId);
      editorStates.set(tabId, { ...current, viewport });
      return { editorStates };
    }),

  // -------------------------------------------------------------------------
  // Tool mode
  // -------------------------------------------------------------------------

  setToolMode: (tabId, mode) => {
    set((state) => {
      const editorStates = new Map(state.editorStates);
      const current = editorStates.get(tabId) ?? createDefaultEditorState(tabId);
      editorStates.set(tabId, { ...current, toolMode: mode });
      // Update connection hint based on mode transition
      const connectionHint =
        mode === "addConnection" ? "Connection Mode -- Click source element" : "";
      // Clear pending connect source when leaving connection mode
      const pendingConnectSource = mode === "addConnection" ? state.pendingConnectSource : null;
      return { editorStates, connectionHint, pendingConnectSource };
    });
    // Dispatch tool mode change to the active editor worker via CustomEvent
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("flowy:setToolMode", {
          detail: { tabId, mode, modeInt: toolModeToInt(mode) },
        }),
      );
    }
  },

  // -------------------------------------------------------------------------
  // Undo/redo state
  // -------------------------------------------------------------------------

  setUndoRedoState: (tabId, canUndo, canRedo) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => (tab.id === tabId ? { ...tab, canUndo, canRedo } : tab)),
    })),

  // -------------------------------------------------------------------------
  // Dirty state
  // -------------------------------------------------------------------------

  setDirty: (tabId, dirty) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => (tab.id === tabId ? { ...tab, dirty } : tab)),
    })),

  // -------------------------------------------------------------------------
  // Worker association
  // -------------------------------------------------------------------------

  setWorkerForTab: (tabId, workerId) =>
    set((state) => {
      const editorStates = new Map(state.editorStates);
      const current = editorStates.get(tabId) ?? createDefaultEditorState(tabId);
      editorStates.set(tabId, { ...current, workerId });
      return { editorStates };
    }),

  // -------------------------------------------------------------------------
  // Convenience getters
  // -------------------------------------------------------------------------

  getActiveTab: () => {
    const { tabs, activeTabId } = get();
    return tabs.find((t) => t.id === activeTabId);
  },

  getActiveEditorState: () => {
    const { editorStates, activeTabId } = get();
    return activeTabId ? editorStates.get(activeTabId) : undefined;
  },

  getEditorState: (tabId) => get().editorStates.get(tabId),

  // -------------------------------------------------------------------------
  // Connection mode
  // -------------------------------------------------------------------------

  setConnectionHint: (hint) => set({ connectionHint: hint }),

  setPendingConnectSource: (info) => set({ pendingConnectSource: info }),

  // -------------------------------------------------------------------------
  // Collapse state (Phase 26, Plan 02)
  // -------------------------------------------------------------------------

  setCollapseState: (tabId, groupId, collapsed, childNames) =>
    set((state) => {
      const editorStates = new Map(state.editorStates);
      const current = editorStates.get(tabId) ?? createDefaultEditorState(tabId);

      const collapsedGroupIds = new Set(current.collapsedGroupIds ?? []);
      const collapsedGroupChildNames = new Map(current.collapsedGroupChildNames ?? []);

      if (collapsed) {
        collapsedGroupIds.add(groupId);
        collapsedGroupChildNames.set(groupId, childNames);
      } else {
        collapsedGroupIds.delete(groupId);
        collapsedGroupChildNames.delete(groupId);
      }

      editorStates.set(tabId, { ...current, collapsedGroupIds, collapsedGroupChildNames });
      return { editorStates };
    }),

  setHoveredGroup: (tabId, hoveredGroup) =>
    set((state) => {
      const editorStates = new Map(state.editorStates);
      const current = editorStates.get(tabId) ?? createDefaultEditorState(tabId);
      editorStates.set(tabId, { ...current, hoveredGroup });
      return { editorStates };
    }),

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------

  reset: () => set(initialState),
}));
